// // create a widget that transforms auth state from LoggedOutState to one of
// // LoggedInState
// // LoggedOutState (i.e. if user goes to reset their password, they remain logged out, and this is the inital state for the reset password widget)

// Internal State:
// LoginTwoFactor
// ResendActivationState
import { any, nothing, Updater, wait, Widget } from 'widgets-for-react'
import {
  Api,
  login_two_factor,
  login as loginToState,
  send_new_pin,
} from './api_to_state'
import {
  AuthState,
  isLoggedOutState,
  LoggedOutState,
  PrivacyKind,
  User,
} from './authState'
import { login_form } from './login_form'
import { login_two_factor_form } from './login_two_factor_form'
import { resendActivateAccount } from './resend_activate_account'
import { options } from './utils'

export interface UserSession {
  readonly user: User
  readonly session: string
  readonly role: string | undefined
}

export function loginWidget(
  options: options,
  api: Api,
  privacyKind: PrivacyKind,
  s0: AuthState,
  originPath: string,
  studentFlow: boolean,
  onSuccess?: Widget<Updater<AuthState>>
): Widget<Updater<AuthState>> {
  if (s0.status === 'logged out' && isLoggedOutState(s0)) {
    return any<Updater<AuthState>>([
      s0.loggedOutState === 'logged out - error account not active'
        ? any<Updater<AuthState>>([
            resendActivateAccount(
              options,
              api,
              s0.login_data.email ?? '',
              s0,
              originPath
            ),
          ])
        : s0.loggedOutState === 'logged out'
        ? login_form(options, s0, false, false, studentFlow)
        : s0.loggedOutState === 'logged out - error too many attempts'
        ? any<Updater<AuthState>>([
            login_form(options, s0, false, true, studentFlow),
          ])
        : s0.loggedOutState === 'logged out - error account blocked'
        ? any<Updater<AuthState>>([
            login_form(options, s0, false, true, studentFlow),
          ])
        : s0.loggedOutState == 'logged out - must reset password'
        ? any<Updater<AuthState>>([
            login_form(options, s0, false, true, studentFlow),
          ])
        : s0.loggedOutState === 'logged out - error login not authorized'
        ? any<Updater<AuthState>>([
            login_form(options, s0, false, true, studentFlow),
          ])
        : s0.loggedOutState === 'logged out - login api error'
        ? any<Updater<AuthState>>([
            wait(500, {
              key: 'login-widget-logging-in-promise',
            })(
              () => (s : AuthState): AuthState =>
                (s as LoggedOutState).loggedOutState !==
                'logged out - login api error'
                  ? s
                  : {
                      ...s,
                      status: 'logged out',
                      loggedOutState: 'logged out - logging in',
                      login_data: s0.login_data,
                    }
            ),
            login_form(options, s0, true, true, studentFlow).never(),
          ])
        : s0.loggedOutState === 'logged out - logging in'
        ? any<Updater<AuthState>>([
            loginToState(api, s0.login_data, privacyKind),
            login_form(options, s0, true, false, studentFlow).never(),
          ])
        : s0.loggedOutState === 'two factor' ||
          s0.loggedOutState === 'two factor - error empty code' ||
          s0.loggedOutState === 'two factor - error too many attempts' ||
          s0.loggedOutState === 'two factor - error wrong code'
        ? login_two_factor_form(options, s0, false)
        : s0.loggedOutState === 'two factor - login api error'
        ? any<Updater<AuthState>>([
            wait(500, {
              key: 'login-widget-two-factor-logging-promise',
            })(
              () => (s: AuthState) : AuthState =>
                (s as LoggedOutState).loggedOutState !==
                'two factor - login api error'
                  ? s
                  : {
                      ...s,
                      loggedOutState: 'two factor - logging',
                      login_data: (s as LoggedOutState).login_data,
                    }
            ),
            login_two_factor_form(options, s0, true).never(),
          ])
        : s0.loggedOutState === 'two factor - logging'
        ? any<Updater<AuthState>>([
            login_two_factor(api, s0.login_data),
            login_two_factor_form(options, s0, true).never(),
          ])
        : s0.loggedOutState === 'two factor - resend api error'
        ? any<Updater<AuthState>>([
            wait(500, {
              key: 'login-widget-two-factor-resending-pin-promise',
            })(
              () => (s : AuthState) : AuthState =>
                (s as LoggedOutState).loggedOutState !==
                'two factor - resend api error'
                  ? s
                  : {
                      ...s,
                      loggedOutState: 'two factor - resending pin',
                      login_data: (s as LoggedOutState).login_data,
                    }
            ),
            login_two_factor_form(options, s0, true).never(),
          ])
        : s0.loggedOutState === 'two factor - resending pin'
        ? any<Updater<AuthState>>([
            send_new_pin(api, s0.login_data),
            login_two_factor_form(options, s0, true).never(),
          ])
        : nothing(),
    ])
  } else if (isLoggedOutState(s0)) {
    return s0.loggedOutState === 'logged in - redirecting' && onSuccess
      ? onSuccess
      : nothing()
  } else {
    return nothing()
  }
}
